import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as seedsApi from '@/api/seeds';
import type {
  PracticeSeedListParams,
  PracticeSessionListParams,
  CreatePracticeSeedInput,
  UpdatePracticeSeedInput,
  AIInteractionListParams,
  BulkDeleteInput,
  BulkUpdateInput,
  BulkCreateInput,
  BulkRestoreInput,
  BulkAssignSeedsInput,
  SeedExportRequest,
  GenerateSeedDraftsInput,
  ImportFormat,
  ImportConflictStrategy,
} from '@/types/seeds';

// ---------- Queries ----------

export function useSeeds(params?: PracticeSeedListParams) {
  return useQuery({
    queryKey: ['seeds', params],
    queryFn: () => seedsApi.listSeeds(params),
    placeholderData: (prev) => prev,
  });
}

export function useSeedDetail(id: string) {
  return useQuery({
    queryKey: ['seeds', id],
    queryFn: () => seedsApi.getSeedDetail(id),
    enabled: !!id,
  });
}

export function usePracticeSessions(params?: PracticeSessionListParams) {
  return useQuery({
    queryKey: ['seeds', 'sessions', params],
    queryFn: () => seedsApi.listPracticeSessions(params),
    placeholderData: (prev) => prev,
  });
}

export function useStudentPersonalizedPractices(
  studentId: string,
  page = 1,
  perPage = 20,
) {
  return useQuery({
    queryKey: ['seeds', 'personalized', studentId, page, perPage],
    queryFn: () => seedsApi.getStudentPersonalizedPractices(studentId, page, perPage),
    enabled: !!studentId,
  });
}

export function useSeedsAnalytics() {
  return useQuery({
    queryKey: ['seeds', 'analytics'],
    queryFn: seedsApi.getSeedsAnalytics,
  });
}

export function useAIInteractions(params?: AIInteractionListParams) {
  return useQuery({
    queryKey: ['admin', 'ai-interactions', params],
    queryFn: () => seedsApi.listAIInteractions(params),
    placeholderData: (prev) => prev,
  });
}

// ---------- Mutations ----------

export function useCreateSeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePracticeSeedInput) => seedsApi.createSeed(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useUpdateSeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePracticeSeedInput }) =>
      seedsApi.updateSeed(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useDeleteSeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => seedsApi.deleteSeed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

// ---------- SPEC-014: Bulk mutations ----------

export function useBulkDeleteSeeds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkDeleteInput) => seedsApi.bulkDeleteSeeds(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useBulkUpdateSeeds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateInput) => seedsApi.bulkUpdateSeeds(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useBulkCreateSeeds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateInput) => seedsApi.bulkCreateSeeds(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useRestoreSeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => seedsApi.restoreSeed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useBulkRestoreSeeds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkRestoreInput) => seedsApi.bulkRestoreSeeds(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useBulkAssignSeeds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkAssignSeedsInput) => seedsApi.bulkAssignSeeds(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'assignments'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
    },
  });
}

// ---------- SPEC-014: Import / export ----------

export function useValidateSeedImport() {
  return useMutation({
    mutationFn: ({ file, format }: { file: File; format: ImportFormat }) =>
      seedsApi.validateSeedImport(file, format),
  });
}

export function useCommitSeedImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      format,
      strategy,
      skipInvalid,
    }: {
      file: File;
      format: ImportFormat;
      strategy: ImportConflictStrategy;
      skipInvalid: boolean;
    }) => seedsApi.commitSeedImport(file, format, strategy, skipInvalid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seeds'] });
    },
  });
}

export function useExportSeeds() {
  return useMutation({
    mutationFn: (request: SeedExportRequest) => seedsApi.exportSeeds(request),
  });
}

// ---------- SPEC-014: AI draft generation ----------

export function useGenerateSeedDrafts() {
  return useMutation({
    mutationFn: (input: GenerateSeedDraftsInput) => seedsApi.generateSeedDrafts(input),
  });
}
